/**
 * CLE-3015 Clear Certified — the tour anchor registry. The ONLY place `data-tour` strings are defined.
 *
 * A component marks a tour target with `data-tour={TourAnchor.X}`; a curriculum step points at it with
 * `anchor: TourAnchor.X`. Renaming or deleting a key is a TypeScript error at both ends, and
 * `anchors.test.ts` fails when a key is no longer used by any component (layer 1 of spec §6).
 *
 * Rows: the `...FirstRow` anchors sit on an element that every table row renders. driver.js resolves a
 * string target with `document.querySelector`, which returns the FIRST match in document order, so the
 * step lands on the first row — the newest estimate, because the Estimates list sorts newest first.
 */
export const TourAnchor = {
  // Module 1 — the hospital view and its Estimates tab
  EstimatesTab: "estimates-tab",
  FacilitySwitcher: "facility-switcher",
  MonthlyCollections: "monthly-collections",
  /** The Estimates table's header row, not the whole table: see m1.04-estimates-table. */
  EstimatesTable: "estimates-table",
  StatusColumn: "status-column",
  SearchBox: "search-box",
  DateFilter: "date-filter",
  StatusFilter: "status-filter",
  CollectPaymentFirstRow: "collect-payment-first-row",
  ViewFirstRow: "view-first-row",
  RowMenuFirstRow: "row-menu-first-row",
  DirectPay: "direct-pay",
  NewEstimate: "new-estimate",

  // Module 2 — the create page (/orders/new)
  OrderType: "order-type",
  PatientDetails: "patient-details",
  VisitCard: "visit-card",
  DuplicateWarning: "duplicate-warning",
  PayerName: "payer-name",
  ServiceType: "service-type",
  MemberId: "member-id",
  RunEligibility: "run-eligibility",
  BenefitFields: "benefit-fields",
  EligibilityMessage: "eligibility-message",
  PreviousOrders: "previous-orders",
  AddSecondaryInsurance: "add-secondary-insurance",
  CptSearch: "cpt-search",
  ServicesTable: "services-table",
  ServiceTypeChanged: "service-type-changed",
  PatientBalanceAmount: "patient-balance-amount",
  EstimateResults: "estimate-results",
  PaymentOptions: "payment-options",
  PatientCommunication: "patient-communication",
  SubmitFooter: "submit-footer",
  CreateAndSend: "create-and-send",
  /** The "Not scheduled" checkbox in the Visit card's top right (CLE-3015 review 1). */
  NotScheduled: "not-scheduled",

  // Module 3 — the Estimate Details page (/orders/:id)
  EstimateHeader: "estimate-header",
  PatientOutreach: "patient-outreach",
  PatientEdit: "patient-edit",
  PatientEditCancel: "patient-edit-cancel",
  VisitEdit: "visit-edit",
  VisitEditCancel: "visit-edit-cancel",
  PatientBenefits: "patient-benefits",
  ServiceDetails: "service-details",
  RedoEstimate: "redo-estimate",
  /**
   * The "..." trigger of the Estimate Details actions menu; Duplicate and Download ABN below are inside it.
   * Download GFE and Collect Payment are header buttons since CLE-3018.
   */
  EstimateActions: "estimate-actions",
  DuplicateEstimate: "duplicate-estimate",
  DuplicateNo: "duplicate-no",
  DownloadAbn: "download-abn",
  DownloadPdf: "download-pdf",
  PdfNote: "pdf-note",
  PdfNoteSave: "pdf-note-save",

  // Module 4 — collecting a payment (the menu, the patient page, its checkout)
  CollectPayment: "collect-payment",
  BillingStatement: "billing-statement",
  WhyOwe: "why-owe",
  PayNowPrice: "pay-now-price",
  PaymentPlanOption: "payment-plan-option",
  PaymentPlanButton: "payment-plan-button",
  PayInFullOption: "pay-in-full-option",
  Continue: "continue",
  PaymentMethodFees: "payment-method-fees",
  CardForm: "card-form",
  SplitPayment: "split-payment",
  TermsCheckbox: "terms-checkbox",
  PayNow: "pay-now",
  BackToDetails: "back-to-details",
  /** The "..." menu's group around "Mark paid via cash" and "Mark paid via check", one highlight (review 1). */
  MarkPaid: "mark-paid",

  // Module 5 — plans, cancelling and outreach on the Estimate Details page
  PaymentPlanEdit: "payment-plan-edit",
  PaymentPlanEditCancel: "payment-plan-edit-cancel",
  CancelEstimate: "cancel-estimate",
  StopOutreach: "stop-outreach",
  SendCustomText: "send-custom-text",
  CustomTextCancel: "custom-text-cancel",
  ResendNotification: "resend-notification",

  // Module 6 — the history card, notes, and the hospital view's other tabs
  ActivityTimeline: "activity-timeline",
  ShowMore: "show-more",
  ViewMessage: "view-message",
  AddNote: "add-note",
  AddNoteText: "add-note-text",
  AddNoteSubmit: "add-note-submit",
  NewestNote: "newest-note",
  ReportsTab: "reports-tab",
  ReportsTable: "reports-table",
  PaidEstimatesDownload: "paid-estimates-download",
  ProvidersTab: "providers-tab",
  ContractsTab: "contracts-tab",
} as const;

export type TourAnchorKey = keyof typeof TourAnchor;
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type TourAnchor = (typeof TourAnchor)[TourAnchorKey];

/** The CSS selector driver.js resolves for an anchor. */
export function tourSelector(anchor: TourAnchor): string {
  return `[data-tour="${anchor}"]`;
}
